/**
 * Bulgarian catalog (Cyrillic) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Bulgarian at a mathematician's register. **Warrant → «Обосновка»**
 * (justification — flagged). `scriptOf('bg')` → cyrillic, paired with the Cyrillic Noto-led
 * font stack. W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories are
 * one / other, so `frameActive` declares exactly those (plus an explicit `=0`). Per
 * INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const BG_CATALOG = {
    locale: 'bg',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Изследване',
        [P2.sectionClaims]: 'Твърдения',
        [P2.sectionIntegrity]: 'Цялост',
        [P2.axisWarrantLabel]: 'Обосновка',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Емпирично',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Определение} interp {Тълкуване} testable {Проверимо} tested {Проверено} refuted {Опровергнато} program {Изследователска програма} philos {Философско} other {Неизвестно}}',
        [P2.axisModalLabel]: 'Модално',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Ниво на сила {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Изследимо',
        [P2.stateRefuted]: 'Опровергнато',
        [P2.stateOutside]: 'Извън обхвата',
        [P2.verdictConflict]: 'В конфликт с {constraints}',
        [P2.verdictOutside]: 'Изисква {missing}',
        [P2.lossRegisterPlain]: 'Опростено до обикновен език — част от точността е пропусната.',
        [P2.lossRegisterKid]: 'Опростено за по-млади читатели — много подробности са пропуснати.',
        [P2.lossProfileDetail]: 'Пълният профил Обосновка / Емпирично / Модално е обобщен тук.',
        [P2.lossConflictDetail]: 'Подробностите за конфликта са съкратени в този изглед.',
        [P2.laneVerified]: 'Проверено',
        [P2.laneUnverified]: 'Непроверено',
        [P2.laneConflict]: 'Конфликтът е доказан',
        [P2.constraintLogic]: 'Логика',
        [P2.constraintLightBends]: 'Светлината се изкривява в гравитационно поле',
        [P2.constraintParallelPostulate]: 'Аксиомата за успоредните прави',
        [P2.claimScalarGravity]: 'Скаларна теория на гравитацията',
        [P2.claimNonEuclidean]: 'Неевклидова геометрия',
        [P2.claimSubstrateSr]: 'Субстратна интерпретация на специалната относителност',
        [P2.edgeExtObservation]: 'Външно наблюдение',
        [P2.edgeResourceSample]: 'Споделен изчислителен ресурс',
        [P2.edgeHeuristicSample]: 'Евристично насочване',
        [P2.edgeSharedSample]: 'Обща основа',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ограничения',
        [P2.sectionEdges]: 'Ребра',
        [P2.sectionFrame]: 'Рамка',
        [P2.sectionExposure]: 'Изложеност',
        [P2.statusAdoptedFormal]: 'Прието (формално)',
        [P2.statusAdoptedInterpretive]: 'Прието (интерпретативно)',
        [P2.statusSuspended]: 'Спряно',
        [P2.edgeEffectConstraint]: 'Образува ограничение (може да опровергае)',
        [P2.edgeEffectResource]: 'Ресурс — без ефект върху присъдите',
        [P2.edgeEffectHeuristic]: 'Евристика — без ефект върху присъдите',
        [P2.edgeEffectShared]: 'Обща основа — без ефект върху присъдите',
        [P2.frameActive]: '{count, plural, =0 {Няма активни ограничения} one {# активно ограничение} other {# активни ограничения}}',
        [P2.exposureNone]: 'Няма интерпретативни зависимости',
        [P2.exposureDep]: 'Зависи от {id}',
        [P2.laneTrackMachine]: 'Машина',
        [P2.laneTrackExpert]: 'Експерт',
        [P2.laneTrackCommunity]: 'Общност',
        [P2.expertAudited]: 'Одитирано',
        [P2.expertInProgress]: 'В ход',
        [P2.expertNotReviewed]: 'Непрегледано',
        [P2.communityFlags]: 'Има сигнали',
        [P2.communityNone]: 'Няма сигнали',
        [P2.controlChallenge]: 'Оспори',
        [P2.controlActivate]: 'Активирай',
        [P2.controlMakeInterpretive]: 'Направи интерпретативно',
        [P2.controlImportEdge]: 'Импортирай ребро',
        [P2.controlPromote]: 'Повиши',
        [P2.controlUndo]: 'Отмени',
        [P2.controlRedo]: 'Повтори',
        [P2.controlRegister]: 'Регистрирай',
        [P2.promoteRejected]: 'Повишението е отхвърлено',
        [P2.promoteAccepted]: 'Повишението е прието',
        [P2.promoteReasonRefutationTerminal]: 'Доказателството не може да изцели опровержение.',
        [P2.promoteReasonKeystone]: 'Ключовото повишение изисква емпирична подкрепа.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Емпиричният вид не е фиксиран.',
        [P2.promoteReasonModalNotFixed]: 'Модалната степен не е фиксирана.',
        [P2.promoteReasonWarrantNotImproved]: 'Степента на обосновка не е подобрена.',
        [P2.evidenceComputation]: "{engine} изчисли {op} = {result} при {config}",
        [P2.lossNumericRounding]: "Резултатът е закръглен до {dp} знака след десетичната запетая",
    },
};
//# sourceMappingURL=bg.js.map